import { AppLayout, BreadcrumbGroup, ContentLayout } from '@amzn/awsui-components-react';
import { Button } from '@amzn/awsui-components-react/polaris';
import {
  Contact,
  EventInput,
  EventType,
  ExternalPartner,
  ShiftInput,
  VCLocationType,
} from '@amzn/red-velvet-api';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getEventDetailsStep,
  getPublishStep,
  getShiftStep,
  getTeamAndPartnershipStep,
} from '../components/events/createEvent/steps';
import { CreateEventWizard } from '../components/events/wizard/createEventWizard';
import { usePublishEvent } from '../hooks/events/publishEvent';
import { useAutoSaveEventDraft } from '../hooks/events/useAutoSaveEventDraft';
import { useWizardStrings } from '../hooks/localization/wizardStrings';
import { useUnsavedChangesBrowserNavigationPrompt } from '../hooks/unsavedChanges';
import { isValidEmail } from '../utils/validation';

export function CreateEvent() {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const wizardStrings = useWizardStrings('createEventWizard', namespaces);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = useRef('');

  const [page, setPage] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { mutate: publishEvent } = usePublishEvent();

  useUnsavedChangesBrowserNavigationPrompt(hasUnsavedChanges);

  const { autoSaveHandler, autoSaveShiftsHandler } = useAutoSaveEventDraft({
    eventId,
    setHasUnsavedChanges,
    onShiftIdsSynced: (shiftIds) => {
      setShiftStepField((prev) =>
        prev.map((shift, i) => (shiftIds[i] ? { ...shift, shiftId: shiftIds[i] } : shift))
      );
    },
  });

  // TODO: If there is already a draft state for this event, then we would want to fetch
  // the values from the draft event and set it as the initial value
  const initialEventDetailsStepField: EventInput = {
    title: '',
    description: '',
    shortDescription: '',
    locationType: VCLocationType.ON_CAMPUS,
    focusArea: '',
    primaryActivity: '',
    eventType: EventType.VOLUNTEERING,
  };
  const [eventDetailsStepField, setEventDetailsStepField] = useState<EventInput>(
    initialEventDetailsStepField
  );

  const initialShiftStepField: ShiftInput[] = [
    {
      startDateTime: new Date(new Date().toDateString()),
      endDateTime: new Date(new Date().toDateString()),
      useEventLocation: true,
    },
  ];
  const [shiftStepField, setShiftStepField] = useState<ShiftInput[]>(initialShiftStepField);

  useEffect(() => {
    const draftEventId = searchParams.get('draftId') ?? '';
    if (draftEventId !== '') {
      eventId.current = draftEventId;
    }
  }, [searchParams]);

  // Validation function for step 0 (Event Details)
  const validateEventDetailsStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!eventDetailsStepField.title?.trim()) {
      newErrors.eventTitle = t('error.eventTitleRequired');
    }
    if (!eventDetailsStepField.description?.trim()) {
      newErrors.eventDescription = t('error.eventDescriptionRequired');
    }
    if (!eventDetailsStepField.shortDescription?.trim()) {
      newErrors.summarizedDescription = t('error.summarizedDescriptionRequired');
    }
    if (!eventDetailsStepField.focusArea?.trim()) {
      newErrors.focusArea = t('error.focusAreaRequired');
    }
    if (!eventDetailsStepField.primaryActivity?.trim()) {
      newErrors.primaryActivity = t('error.primaryActivityRequired');
    }
    if (eventDetailsStepField.usedAciGrant === undefined) {
      newErrors.usedAciGrant = t('error.usedAciGrantRequired');
    }

    // Location-specific validation
    if (eventDetailsStepField.locationType === VCLocationType.ON_CAMPUS) {
      if (!eventDetailsStepField.location?.amazonBuilding?.trim()) {
        newErrors.building = t('error.buildingRequired');
      }
      if (!eventDetailsStepField.location?.amazonBuildingRoom?.trim()) {
        newErrors.room = t('error.roomRequired');
      }
    } else if (eventDetailsStepField.locationType === VCLocationType.OFF_CAMPUS) {
      if (!eventDetailsStepField.location?.address_string?.trim()) {
        newErrors.location = t('error.locationRequired');
      }
      if (!eventDetailsStepField.location?.name?.trim()) {
        newErrors.locationName = t('error.locationNameRequired');
      }
    }

    // ACI Grant specific validation
    if (eventDetailsStepField.usedAciGrant === true) {
      if (!eventDetailsStepField.grantId?.trim()) {
        newErrors.grantId = t('error.grantIdRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate current step before allowing navigation
  const validateCurrentStep = (currentStepIndex: number): boolean => {
    switch (currentStepIndex) {
      case 0:
        return validateEventDetailsStep();
      case 1:
        return validateShiftStep();
      case 2:
        return validateTeamStep();
      default:
        return true;
    }
  };

  // Validation function for step 2 (Team & Partnership)
  const validateTeamStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (eventDetailsStepField.externalPlanningPartner === ExternalPartner.OTHER) {
      if (!eventDetailsStepField.otherPartner?.trim()) {
        newErrors.otherPartnerName = t('error.otherPartnerRequired');
      } else if (eventDetailsStepField.otherPartner.length > 30) {
        newErrors.otherPartnerName = t('error.inputTooLong', { maxLength: 30 });
      }
    }

    const validateContacts = (contacts: Contact[], prefix: string) => {
      contacts.forEach((contact, i) => {
        if (contact.email && !isValidEmail(contact.email)) {
          newErrors[`${prefix}_${i}_email`] = t('error.invalidEmail');
        }
      });
    };

    validateContacts(eventDetailsStepField.amazonContacts ?? [], 'amazonContact');
    validateContacts(eventDetailsStepField.externalContacts ?? [], 'externalContact');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateEventFieldHandler = (eventInput: EventInput) => {
    setEventDetailsStepField(eventInput);
    autoSaveHandler(eventInput);
  };

  // Validation function for step 1 (Shifts)
  const validateShiftStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    shiftStepField.forEach((shift, index) => {
      const isScheduled = (shift.shiftTimingType ?? 'scheduled') === 'scheduled';

      if (!shift.startDateTime) {
        newErrors[`shift_${index}_date`] = t('error.shiftDateRequired');
        return;
      }

      if (isScheduled) {
        // Both times at the same value (e.g. default midnight) means user didn't set times
        if (shift.endDateTime && shift.startDateTime.getTime() === shift.endDateTime.getTime()) {
          newErrors[`shift_${index}_startTime`] = t('error.shiftTimeRequired');
        } else if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
          newErrors[`shift_${index}_endTime`] = t('error.shiftEndTimeBeforeStart');
        }
      } else {
        // Flexible: end date must be strictly after start date
        if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
          newErrors[`shift_${index}_endDate`] = t('error.shiftEndDateBeforeStart');
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const steps = [
    getEventDetailsStep(
      t('eventDetailsStep.eventDetails'),
      t('eventDetailsStep.eventDetailsSubText'),
      eventDetailsStepField,
      updateEventFieldHandler,
      errors
    ),
    getShiftStep(
      t('shiftStep.stepTitle'),
      t('shiftStep.headerDescription'),
      shiftStepField,
      (args) => {
        setShiftStepField(args);
        autoSaveShiftsHandler(args);
      },
      eventDetailsStepField,
      updateEventFieldHandler,
      errors
    ),
    getTeamAndPartnershipStep(
      t('teamPartnershipStep.stepTitle'),
      t('teamPartnershipStep.stepDescription'),
      eventDetailsStepField,
      updateEventFieldHandler,
      errors
    ),
    getPublishStep(
      t('reviewPublishStep.stepTitle'),
      eventId.current,
      t('reviewPublishStep.preview'),
      {
        currentEventFields: eventDetailsStepField,
        updateEventFieldHandler: updateEventFieldHandler,
        setActiveStepIndex: (index: number) => setPage(index),
        errors,
      }
    ),
  ];

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={true}
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: t('breadcrumbs.myActivity'), href: '/my-activity' },
            { text: t('breadcrumbs.coordinatorActivity'), href: '/coordinator-activity' },
            { text: t('breadcrumbs.createEvent'), href: '#' },
          ]}
        />
      }
      content={
        <ContentLayout>
          <CreateEventWizard
            i18nStrings={{
              ...wizardStrings,
              // Next button should match the title of the next step
              nextButton: t([`createEventWizard.nextButton`], {
                title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
              }),
              submitButton: t('createEventWizard.submitButton'),
            }}
            steps={steps}
            onCancel={() => {
              navigate('/coordinator-activity');
            }}
            onNavigate={({ detail }) => {
              // Validate current step but does not prevent allowing navigation forward
              if (detail.requestedStepIndex > page) {
                validateCurrentStep(page);
              } else {
                setErrors({});
              }

              setPage(detail.requestedStepIndex);
            }}
            onSubmit={() => {
              if (!validateCurrentStep(page)) {
                // Prevent submit if validation fails
                return;
              }
              if (eventId.current !== '') {
                publishEvent(
                  { eventId: eventId.current },
                  {
                    onSuccess: (response) => {
                      eventId.current = response.eventId;
                      navigate(`/view-event?id=${response.eventId}`);
                    },
                    onError: () => {
                      autoSaveHandler(eventDetailsStepField);
                      setErrors({
                        ...errors,
                        publishError: t('error.publishErrorMsg'),
                      });
                    },
                  }
                );
              }
            }}
            activeStepIndex={page}
            secondaryActions={
              <Button onClick={() => autoSaveHandler(eventDetailsStepField)}>
                {t('createEventWizard.saveDraft')}
              </Button>
            }
          />
        </ContentLayout>
      }
      contentType="wizard"
    />
  );
}
